import { useQuery } from '@tanstack/react-query';
import { useApi, type PropertyGroupResponse } from '@/shared/api';
import type { ObjectArea } from './objectAreas';
import type { AreaStatusInput } from './objectAreaStatus';

/** Leiste und Übersicht sind Auskunft, keine Live-Anzeige; die Bereiche laden beim Öffnen frisch. */
const STATUS_STALE_MS = 30_000;

/**
 * Der Stand je Bereich für Leiste und Übersicht. Geladen wird nur, was der Nutzer ohnehin öffnen
 * darf, und zwar unter denselben Query-Keys wie in den Bereichen — ein Wechsel dorthin lädt
 * nichts doppelt, und jedes Speichern dort aktualisiert den Stand mit. Was (noch) nicht geladen
 * ist, bleibt `undefined` und erzeugt keine Aussage.
 */
export function useObjectStatusData(
  group: PropertyGroupResponse,
  areas: ObjectArea[],
): AreaStatusInput {
  const api = useApi();
  const id = group.id;
  const visible = new Set(areas.map((area) => area.id));

  const arrival = useQuery({
    queryKey: ['property-groups', id, 'arrival-steps', 'Arrival'],
    queryFn: () => api.arrivalSteps.listForObject(id, 'Arrival'),
    enabled: visible.has('anfahrt'),
    staleTime: STATUS_STALE_MS,
  });
  const alternatives = useQuery({
    queryKey: ['property-groups', id, 'arrival-steps', 'ParkingAlternatives'],
    queryFn: () => api.arrivalSteps.listForObject(id, 'ParkingAlternatives'),
    enabled: visible.has('parken'),
    staleTime: STATUS_STALE_MS,
  });
  const parking = useQuery({
    queryKey: ['property-groups', id, 'parking-spots'],
    queryFn: () => api.parking.listSpots(id),
    enabled: visible.has('parken'),
    staleTime: STATUS_STALE_MS,
  });
  const catalog = useQuery({
    queryKey: ['property-groups', id, 'guest-experience', 'messages'],
    queryFn: () => api.propertyGroups.guestExperience.messages.get(id),
    enabled: visible.has('nachrichten'),
    staleTime: STATUS_STALE_MS,
  });
  const branding = useQuery({
    queryKey: ['property-groups', id, 'guest-experience', 'branding'],
    queryFn: () => api.propertyGroups.guestExperience.branding.get(id),
    enabled: visible.has('gastportal'),
    staleTime: STATUS_STALE_MS,
  });
  const publication = useQuery({
    queryKey: ['property-groups', id, 'publication'],
    queryFn: () => api.channex.publicationState(id),
    enabled: visible.has('veroeffentlichung'),
    staleTime: STATUS_STALE_MS,
  });

  return {
    group,
    arrivalSteps: arrival.data?.steps.length,
    parkingSpots: parking.data?.spots.length,
    parkingAlternatives: alternatives.data?.steps.length,
    catalog: catalog.data,
    branding: branding.data,
    publication: publication.data,
    unavailable: [
      arrival.isError ? 'anfahrt' : null,
      parking.isError || alternatives.isError ? 'parken' : null,
    ].filter((id): id is 'anfahrt' | 'parken' => !!id),
  };
}
